import { FormEvent, useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { EmailInput } from "@/components/EmailInput";
import { useLocale } from "@/i18n";
import { api, ApiError } from "@/lib/api";
import { saveGuestSession, type GuestSession } from "@/lib/guestAuth";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import { useTouristIntegrationEnabled } from "@/lib/useTouristIntegrationEnabled";

type Phase = "email" | "code";

function resolveReturnTo(state: unknown): string {
  if (
    state &&
    typeof state === "object" &&
    "returnTo" in state &&
    typeof (state as { returnTo: unknown }).returnTo === "string"
  ) {
    const path = (state as { returnTo: string }).returnTo;
    if (path.startsWith("/") && !path.startsWith("//")) {
      return path;
    }
  }
  return "/portal";
}

function isBookingReturnPath(path: string): boolean {
  return path === "/buchen" || path === "/" || path === "/en" || path === "/en/buchen";
}

export function GuestLoginPage() {
  const { locale, messages, localePath } = useLocale();
  const { siteName, logoUrl } = useCampsiteBranding();
  const { enabled: touristIntegrationEnabled, loading: touristConfigLoading } =
    useTouristIntegrationEnabled();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = resolveReturnTo(location.state) || localePath("/portal");
  const backToBooking = isBookingReturnPath(returnTo);
  const [phase, setPhase] = useState<Phase>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function requestCode(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await api.post<{ message: string }>("/api/v1/public/guest/auth/request-code", {
        email: email.trim(),
      });
      setMessage(res.message);
      setPhase("code");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : messages.login.requestError);
    } finally {
      setLoading(false);
    }
  }

  async function verifyCode(e: FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const session = await api.post<GuestSession>("/api/v1/public/guest/auth/verify", {
        email: email.trim(),
        code: code.trim(),
      });
      saveGuestSession(session);
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : messages.login.loginError);
    } finally {
      setLoading(false);
    }
  }

  if (!touristConfigLoading && touristIntegrationEnabled) {
    return <Navigate to={localePath("/buchen")} replace />;
  }

  return (
    <div className="mx-auto min-h-screen max-w-lg px-4 py-10">
      <BookingBrandHeader
        siteName={siteName}
        logoUrl={logoUrl}
        title={messages.login.title}
        subtitle={
          backToBooking ? messages.login.subtitleBooking : messages.login.subtitleDefault
        }
        className="mb-8"
      />

      <div className="bk-card space-y-4">
        {backToBooking ? (
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-950">
            <p className="font-semibold">{messages.guestAuth.returningGuests}</p>
            <p className="mt-1">{messages.guestAuth.alreadyBookedHint}</p>
          </div>
        ) : null}
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
            {error}
          </div>
        )}
        {message && (
          <div className="rounded-lg border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-900">
            {message}
          </div>
        )}

        {phase === "email" ? (
          <form onSubmit={requestCode} className="space-y-4">
            <label className="block text-sm font-medium text-slate-700" htmlFor="login-email">
              {messages.login.emailLabel}
              <EmailInput
                id="login-email"
                value={email}
                onChange={setEmail}
                required
                className="bk-input mt-1 w-full"
              />
            </label>
            <button type="submit" className="bk-btn-primary w-full" disabled={loading}>
              {loading ? messages.login.requestingCode : messages.login.requestCode}
            </button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="space-y-4">
            <p className="text-sm text-slate-600">
              {messages.login.codeLabel} <strong>{email}</strong>
            </p>
            <label className="block text-sm font-medium text-slate-700" htmlFor="login-code">
              {messages.login.codeLabel}
            </label>
            <input
              id="login-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              maxLength={8}
              className="bk-input w-full text-center text-2xl tracking-widest"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
            />
            <button type="submit" className="bk-btn-primary w-full" disabled={loading}>
              {loading ? messages.login.verifying : messages.login.verify}
            </button>
            <button
              type="button"
              className="bk-btn-secondary w-full"
              disabled={loading}
              onClick={() => {
                setPhase("email");
                setCode("");
                setMessage(null);
              }}
            >
              {locale === "en" ? "Use a different email" : "Andere E-Mail verwenden"}
            </button>
          </form>
        )}

        <p className="text-center text-sm text-slate-500">
          <Link
            to={backToBooking ? returnTo : localePath("/")}
            className="text-brand-700 underline"
          >
            {backToBooking ? messages.login.backToBooking : messages.login.toBooking}
          </Link>
        </p>
      </div>
    </div>
  );
}
